import * as hardware from "@client/hardware";
import {classNames} from "@reactivated";
import {AnimatePresence, type Transition, motion} from "motion/react";
import React from "react";
import {flushSync} from "react-dom";

// Multi-state delete button: Delete, Tap to confirm, Deleting, Deleted.
const DELETE_STATES = {
    idle: "Delete",
    confirm: "Tap to confirm",
    processing: "Deleting",
    success: "Deleted",
    error: "Error",
} as const;

export type DeleteState = keyof typeof DELETE_STATES;

const SPRING_CONFIG: Transition = {
    type: "spring",
    stiffness: 600,
    damping: 30,
};

export function DeleteButtonLabel({state}: {state: DeleteState}) {
    const [labelWidth, setLabelWidth] = React.useState(0);
    const measureRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (measureRef.current) {
            // offsetWidth: a parent mid scale animation must not shrink the label.
            setLabelWidth(measureRef.current.offsetWidth);
        }
    }, [state]);

    return (
        <>
            <div ref={measureRef} className="invisible absolute whitespace-nowrap">
                {DELETE_STATES[state]}
            </div>
            <motion.span
                layout
                style={{position: "relative"}}
                animate={{width: labelWidth}}
                transition={SPRING_CONFIG}
            >
                <AnimatePresence mode="sync" initial={false}>
                    <motion.div
                        key={state}
                        style={{textWrap: "nowrap"}}
                        initial={{
                            y: -20,
                            opacity: 0,
                            filter: "blur(8px)",
                            position: "absolute",
                        }}
                        animate={{
                            y: 0,
                            opacity: 1,
                            filter: "blur(0px)",
                            position: "relative",
                        }}
                        exit={{
                            y: 20,
                            opacity: 0,
                            filter: "blur(8px)",
                            position: "absolute",
                        }}
                        transition={{duration: 0.2, ease: "easeInOut"}}
                    >
                        {DELETE_STATES[state]}
                    </motion.div>
                </AnimatePresence>
            </motion.span>
        </>
    );
}

// The two taps, the progress and the 3 s fall back to idle, for any surface that
// shows the label.
export function useMultiStateDelete({
    onDelete,
    onClose,
}: {
    onDelete: () => Promise<void>;
    onClose: () => void;
}) {
    const [state, setState] = React.useState<DeleteState>("idle");
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleClick = async () => {
        void hardware.haptic();
        if (state === "idle") {
            setState("confirm");
            timeoutRef.current = setTimeout(() => {
                flushSync(() => setState("idle"));
            }, 3000);
        } else if (state === "confirm") {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            setState("processing");
            try {
                await onDelete();
                setState("success");
                setTimeout(() => {
                    onClose();
                }, 500);
            } catch {
                setState("error");
                setTimeout(() => {
                    flushSync(() => setState("idle"));
                }, 2000);
            }
        }
    };

    return {state, handleClick};
}

export function MultiStateDeleteButton({
    onDelete,
    onClose,
}: {
    onDelete: () => Promise<void>;
    onClose: () => void;
}) {
    const {state, handleClick} = useMultiStateDelete({onDelete, onClose});

    const bgColor = {
        idle: "bg-red-100 hover:bg-red-200 text-red-700",
        confirm: "bg-red-500 hover:bg-red-600 text-white",
        processing: "bg-gray-400 text-white",
        success: "bg-green-500 text-white",
        error: "bg-red-600 text-white",
    }[state];

    return (
        <motion.button
            type="button"
            onClick={handleClick}
            disabled={state === "processing" || state === "success"}
            className={classNames(
                "flex items-center justify-center overflow-hidden rounded-full px-5 py-3 font-medium transition-colors",
                bgColor,
                "disabled:cursor-not-allowed",
            )}
            style={{gap: state === "idle" ? 0 : 8, willChange: "transform, filter"}}
        >
            <DeleteButtonLabel state={state} />
        </motion.button>
    );
}
